// One aircraft tick (spec §7). Pure: returns a new SimState.
//
// Cruise laws (why they are what they are): a fixed-altitude, fixed-power cruise reaches only ~69 % of the
// Breguet range over Voyager's 3.6× weight ratio and ~86 % over the 777's 1.9×, so the model flies the way the
// record crews did. Shaft-engine aircraft set the chosen power at gross weight and then HOLD THAT LIFT
// COEFFICIENT (indicated airspeed), throttling back as fuel burns — the pilot's "set cruise speed and trim".
// Jets fly at the chosen thrust limit with an autothrottle Mach cap and are offered optimum-altitude step climbs.
import type { Controls, SimState, AirEnv, Mixture, Phase } from '../types';
import { G, isaDensity, speedOfSound, ftToM, mToFt, msToKt } from './atmosphere';
import type { ResolvedCraft, AirEff } from './resolve';
import { airEff, isJet, maxMassKg, resolveBuild } from './resolve';
import {
  dragN, shaftPowerAvailableW, thrustAvailableN, propEfficiency, vStallMs, vBestLdMs, solvePropSpeed,
  solveJetSpeed, tsfcFactor, type DragOpts,
} from './aero';

export { resolveBuild };

/** Spec §7 mixture multipliers on BSFC and on power. */
export const MIXTURE_BSFC: Record<Mixture, number> = { rich: 1, peak: 0.9, lop: 0.85 };
export const MIXTURE_POWER: Record<Mixture, number> = { rich: 1, peak: 1, lop: 0.92 };
/** Below ≈ 100 ft/min the climb stops where it is (service-ceiling behaviour). */
const CLIMB_MIN_ROC_MS = 0.5;
/** ≈ 500 ft/min when the target altitude is below the current one, or when level flight cannot be held. */
const DESCENT_RATE_MS = 2.5;
export const STILL_AIR: AirEnv = { windAlongKt: () => 0, dragPenalty: 0 };

export interface CruisePoint {
  /** false = the power/thrust cannot hold this altitude; the numbers are the best-L/D fallback. */
  ok: boolean;
  vMs: number;
  fuelKgPerH: number;
  /** Shaft power (W) or thrust (N) actually used. */
  outputW: number;
  load: number;
  groundKt: number;
  srNmPerKg: number;
}

export interface ClimbPoint { vMs: number; rocMs: number; fuelKgPerH: number; load: number }

type ShaftKind = 'piston' | 'turboprop';

function shaftKind(rc: ResolvedCraft): ShaftKind {
  return rc.engine.kind === 'turboprop' ? 'turboprop' : 'piston';
}

function mixtureOf(rc: ResolvedCraft, c: Controls): Mixture {
  return rc.engine.kind === 'piston' ? (c.mixture ?? 'rich') : 'rich';
}

function dragOpts(eff: AirEff, jettisoned: boolean, env: AirEnv): DragOpts {
  return { cd0Extra: jettisoned ? 0 : eff.storesCd0, dragPenalty: env.dragPenalty };
}

/** Power limit (W) the pilot has set: min(power × rated, available at altitude) × mixture power multiplier. */
function shaftPowerLimitW(rc: ResolvedCraft, eff: AirEff, altM: number, c: Controls): number {
  const avail = shaftPowerAvailableW(eff, shaftKind(rc), altM);
  const set = (c.power ?? 0.65) * eff.ratedPowerKw * 1000 * eff.engineCount;
  return Math.min(set, avail) * MIXTURE_POWER[mixtureOf(rc, c)];
}

/** Lift coefficient of the chosen cruise at gross weight — the one the pilot then holds (null: not flyable). */
export function targetCl(rc: ResolvedCraft, c: Controls, altM: number, o: DragOpts): number | null {
  const eff = airEff(rc);
  const m = maxMassKg(eff);
  const v = solvePropSpeed(eff, m, altM, shaftPowerLimitW(rc, eff, altM, c), o);
  if (v === null) return null;
  return (m * G) / (0.5 * isaDensity(altM) * v * v * eff.wingAreaM2);
}

/** Steady cruise at this mass and altitude with these controls. */
export function cruisePoint(
  rc: ResolvedCraft, massKg: number, altM: number, c: Controls, env: AirEnv, jettisoned: boolean,
): CruisePoint {
  const eff = airEff(rc);
  const o = dragOpts(eff, jettisoned, env);
  const rho = isaDensity(altM), a = speedOfSound(altM);
  const wind = env.windAlongKt(mToFt(altM));
  let ok = true, v: number | null = null, output = 0, fuel = 0, load = 0;
  if (!isJet(rc)) {
    const mix = mixtureOf(rc, c);
    const pLimit = shaftPowerLimitW(rc, eff, altM, c);
    const cl = targetCl(rc, c, altM, o);
    if (cl !== null) {
      const vt = Math.sqrt((2 * massKg * G) / (rho * eff.wingAreaM2 * cl));
      if (vt >= 1.1 * vStallMs(eff, massKg, rho)) {
        const pReq = (dragN(eff, massKg, rho, a, vt, o) * vt) / propEfficiency(eff, vt);
        if (pReq <= pLimit) { v = vt; output = pReq; }
      }
    }
    if (v === null) { v = solvePropSpeed(eff, massKg, altM, pLimit, o); output = pLimit; }
    if (v === null) {
      ok = false;
      v = vBestLdMs(eff, massKg, rho, o.cd0Extra);
      output = shaftPowerAvailableW(eff, shaftKind(rc), altM) * MIXTURE_POWER[mix];
    }
    fuel = eff.bsfcKgPerKwh * MIXTURE_BSFC[mix] * (output / 1000);
    load = output / (eff.ratedPowerKw * 1000 * eff.engineCount);
  } else {
    const ab = !!c.afterburner && eff.afterburnerThrustN > 0;
    const frac = c.thrust ?? 0.6;
    const thrustAt = (mach: number): number => frac * thrustAvailableN(eff, altM, mach, ab);
    v = solveJetSpeed(eff, massKg, altM, thrustAt, o);
    if (v === null) {
      ok = false;
      v = vBestLdMs(eff, massKg, rho, o.cd0Extra);
      output = thrustAvailableN(eff, altM, v / a, ab);
    } else {
      // Autothrottle: at the Mach cap the commanded thrust exceeds drag, so only drag is used.
      output = Math.min(thrustAt(v / a), dragN(eff, massKg, rho, a, v, o));
    }
    fuel = (ab ? eff.tsfcAbKgPerNh : eff.tsfcKgPerNh * tsfcFactor(eff, altM, v / a)) * output;
    load = Math.min(1, output / thrustAvailableN(eff, altM, v / a, false));
  }
  const groundKt = Math.max(0, msToKt(v) + wind);
  return { ok, vMs: v, fuelKgPerH: fuel, outputW: output, load, groundKt, srNmPerKg: fuel > 0 ? groundKt / fuel : 0 };
}

/** Full-power climb at max(1.3·Vstall, best-L/D speed); shaft engines full rich. */
export function climbPoint(rc: ResolvedCraft, massKg: number, altM: number, c: Controls, jettisoned: boolean): ClimbPoint {
  const eff = airEff(rc);
  const o = dragOpts(eff, jettisoned, STILL_AIR);
  const rho = isaDensity(altM), a = speedOfSound(altM);
  const v = Math.max(1.3 * vStallMs(eff, massKg, rho), vBestLdMs(eff, massKg, rho, o.cd0Extra));
  const d = dragN(eff, massKg, rho, a, v, o);
  if (!isJet(rc)) {
    const pAvail = shaftPowerAvailableW(eff, shaftKind(rc), altM);
    const roc = (propEfficiency(eff, v) * pAvail - d * v) / (massKg * G);
    return { vMs: v, rocMs: roc, fuelKgPerH: eff.bsfcKgPerKwh * (pAvail / 1000), load: 1 };
  }
  const ab = !!c.afterburner && eff.afterburnerThrustN > 0;
  const t = thrustAvailableN(eff, altM, v / a, ab);
  const tsfc = ab ? eff.tsfcAbKgPerNh : eff.tsfcKgPerNh * tsfcFactor(eff, altM, v / a);
  return { vMs: v, rocMs: ((t - d) * v) / (massKg * G), fuelKgPerH: tsfc * t, load: 1 };
}

/** Turbofans: N-scale label 2000 + 8000·load (audio bank convention). Fixed pitch: propeller law rpm ∝ ∛load.
 *  Constant speed: rated in the climb, the selected propRpm in cruise (else a 50–100 % schedule). */
function rpmFor(rc: ResolvedCraft, c: Controls, load: number, phase: Phase): number {
  const e = rc.engine;
  const l = Math.max(0, Math.min(1, load));
  if (isJet(rc)) return 2000 + 8000 * l;
  if (rc.air?.propeller === 'constant-speed') {
    if (phase === 'climb') return e.ratedRpm;
    return c.propRpm ?? e.idleRpm + (e.ratedRpm - e.idleRpm) * (0.5 + 0.5 * l);
  }
  return Math.max(e.idleRpm, e.ratedRpm * Math.cbrt(l));
}

export function initialState(rc: ResolvedCraft, _controls: Controls): SimState {
  const eff = airEff(rc);
  return {
    timeS: 0, distanceNm: 0, fuelKg: eff.fuelCapacityKg, massKg: maxMassKg(eff), altitudeFt: 0, tasKt: 0, groundKt: 0,
    windOrCurrentKt: 0, rpm: rc.engine.idleRpm, load: 0, fuelFlowKgPerH: 0, specificRangeNmPerKg: 0, fouling: 0,
    seaState: 0, phase: 'climb', segmentIndex: 0, storesJettisoned: false,
  };
}

export function stepAircraft(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv, dtS: number): SimState {
  const eff = airEff(rc);
  if (s.phase === 'ended' || s.fuelKg <= 0) return { ...s, phase: 'ended', fuelKg: Math.max(0, s.fuelKg) };
  const altM = ftToM(s.altitudeFt);
  const capM = Math.min(ftToM(c.altitudeFt ?? s.altitudeFt), eff.ceilingM);
  let phase: Phase = s.phase;
  let newAltM = altM;
  let vMs = 0, fuelFlow = 0, load = 0, groundKt = 0, wind = 0;
  if (phase === 'climb') {
    const cp = climbPoint(rc, s.massKg, altM, c, s.storesJettisoned);
    if (cp.rocMs <= CLIMB_MIN_ROC_MS || altM >= capM - 0.5) {
      phase = 'cruise';
    } else {
      newAltM = Math.min(capM, altM + cp.rocMs * dtS);
      vMs = cp.vMs; fuelFlow = cp.fuelKgPerH; load = cp.load;
      wind = env.windAlongKt(s.altitudeFt);
      groundKt = Math.max(0, msToKt(vMs) + wind);
      if (newAltM >= capM - 0.5) phase = 'cruise';
    }
  }
  if (phase === 'cruise' && vMs === 0) {
    if (altM > capM + 15) newAltM = Math.max(capM, altM - DESCENT_RATE_MS * dtS);
    const pt = cruisePoint(rc, s.massKg, newAltM, c, env, s.storesJettisoned);
    if (!pt.ok) newAltM = Math.max(0, newAltM - DESCENT_RATE_MS * dtS);
    vMs = pt.vMs; fuelFlow = pt.fuelKgPerH; load = pt.load; groundKt = pt.groundKt;
    wind = env.windAlongKt(mToFt(newAltM));
  }
  // Integrate; a step that would empty the tank is shortened to the dry-tank moment.
  let dt = dtS;
  const burn = (fuelFlow * dtS) / 3600;
  let fuel = s.fuelKg - burn;
  if (fuel <= 0) { dt = burn > 0 ? dtS * (s.fuelKg / burn) : dtS; fuel = 0; phase = 'ended'; }
  const distance = s.distanceNm + (groundKt * dt) / 3600;
  let jettisoned = s.storesJettisoned;
  if (!jettisoned && eff.storesFuelKg > 0 && phase !== 'climb' && fuel <= eff.fuelCapacityKg - eff.storesFuelKg) jettisoned = true;
  const massKg = eff.emptyMassKg - (jettisoned ? eff.storesMassKg : 0) + eff.payloadKg + fuel;
  return {
    ...s,
    timeS: s.timeS + dt,
    distanceNm: distance,
    fuelKg: fuel,
    massKg,
    altitudeFt: mToFt(newAltM),
    tasKt: msToKt(vMs),
    groundKt,
    windOrCurrentKt: wind,
    rpm: rpmFor(rc, c, load, phase),
    load,
    fuelFlowKgPerH: fuelFlow,
    specificRangeNmPerKg: fuelFlow > 0 ? groundKt / fuelFlow : 0,
    phase,
    storesJettisoned: jettisoned,
  };
}

/** Altitude (ft) maximising specific range at the current mass, scanned 0–45,000 ft in 2,000 ft steps (spec §7). */
export function optimumAltitudeFt(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv = STILL_AIR): number {
  const eff = airEff(rc);
  const top = Math.min(45000, mToFt(eff.ceilingM));
  let best = s.altitudeFt;
  const here = cruisePoint(rc, s.massKg, ftToM(s.altitudeFt), c, env, s.storesJettisoned);
  let bestSr = here.ok ? here.srNmPerKg : 0;
  for (let h = 0; h <= top + 1; h += 2000) {
    const pt = cruisePoint(rc, s.massKg, ftToM(h), c, env, s.storesJettisoned);
    if (pt.ok && pt.srNmPerKg > bestSr * 1.001) { best = h; bestSr = pt.srNmPerKg; }
  }
  return best;
}
